import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Copy, KeyRound } from 'lucide-react'
import { api } from '../lib/api'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'

/**
 * „Tokenul meu de API" (01.11.2026) — pentru programele care îți citesc task-urile din „Ale mele”,
 * cum e Directorul lui Mădălin. Tokenul doar citește: nu schimbă nimic și nu deschide alt ecran.
 * Contractul: `docs/api-export-taskuri.md`.
 *
 * Tokenul se arată o singură dată, la generare. Serverul ține doar amprenta lui.
 */
interface Token {
  id: number
  prefix: string
  createdAt: string
  expiresAt: string
  lastUsedAt: string | null
}

function mesaj(e: unknown): string {
  const r = (e as { response?: { data?: { error?: { message?: string } } } })?.response
  return r?.data?.error?.message ?? 'A apărut o eroare'
}

const clipa = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('ro-RO', { dateStyle: 'short', timeStyle: 'short' })
    : 'niciodată'

export function TokenulMeuPage() {
  const qc = useQueryClient()
  const [secret, setSecret] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['tokenul-meu'],
    queryFn: async () => (await api.get<{ token: Token | null }>('/tokenul-meu')).data.token,
    retry: false,
  })
  const faraDrept = (error as { response?: { status?: number } } | null)?.response?.status === 403

  const genereaza = useMutation({
    mutationFn: async () => (await api.post<{ secret: string }>('/tokenul-meu', {})).data,
    onSuccess: (r) => {
      setSecret(r.secret)
      void qc.invalidateQueries({ queryKey: ['tokenul-meu'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const revoca = useMutation({
    mutationFn: async (id: number) => api.delete(`/tokenul-meu/${id}`),
    onSuccess: () => {
      setSecret(null)
      toast.success('Tokenul a fost revocat. Nu mai merge nicăieri.')
      void qc.invalidateQueries({ queryKey: ['tokenul-meu'] })
    },
    onError: (e) => toast.error(mesaj(e)),
  })

  const copiaza = async () => {
    if (!secret) return
    try {
      await navigator.clipboard.writeText(secret)
      toast.success('Copiat')
    } catch {
      toast.error('Nu s-a putut copia. Selectează-l și copiază-l de mână.')
    }
  }

  return (
    <div className="max-w-2xl space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Tokenul meu de API</h1>
        <p className="mt-0.5 text-sm text-b4l-text-muted">
          Pentru un program care îți citește task-urile din „Ale mele”. Doar citește — nu mută, nu
          comentează, nu deschide alte ecrane.
        </p>
      </div>

      {faraDrept ? (
        <Card>
          <p className="p-4 text-sm text-b4l-text-muted">
            Tokenul de API e doar pentru administratorii care au primit dreptul pentru el.
          </p>
        </Card>
      ) : error && !secret ? (
        // Fara raspuns nu stim daca exista un token: „Genereaza” l-ar revoca pe nevazute. Un token
        // abia generat ramane insa pe ecran, chiar daca recitirea de dupa pica — nu se mai arata.
        <Card>
          <p className="p-4 text-sm text-b4l-red-dark">
            Nu s-a putut citi tokenul: {mesaj(error)}. Reîncarcă pagina.
          </p>
        </Card>
      ) : (
        <Card>
          <div className="space-y-3 p-4 text-sm">
            {secret && (
              <div className="space-y-2 rounded-lg bg-b4l-amber-light p-3 text-b4l-amber-dark">
                <p className="font-medium">Copiază tokenul acum. Nu se mai arată a doua oară.</p>
                <div className="flex items-center gap-2">
                  <code className="min-w-0 flex-1 select-all break-all rounded bg-b4l-bg px-2 py-1 text-xs text-b4l-text">
                    {secret}
                  </code>
                  <Button
                    variant="secondary"
                    onClick={() => void copiaza()}
                    aria-label="Copiază tokenul"
                  >
                    <Copy size={14} />
                  </Button>
                </div>
                <p className="text-xs">
                  Pune-l doar în fișierul de configurare al programului. Nu-l trimite pe mail sau pe
                  chat.
                </p>
              </div>
            )}

            {isLoading ? (
              <p className="text-b4l-text-muted">Se încarcă…</p>
            ) : data ? (
              <div className="space-y-1">
                <p className="flex items-center gap-2 font-medium">
                  <KeyRound size={14} /> Token activ <code className="text-xs">{data.prefix}…</code>
                </p>
                <p className="text-xs text-b4l-text-muted">
                  Generat: {clipa(data.createdAt)} · Expiră: {clipa(data.expiresAt)} · Ultima
                  folosire: {clipa(data.lastUsedAt)}
                </p>
              </div>
            ) : (
              <p className="text-b4l-text-muted">Nu ai niciun token activ.</p>
            )}

            <div className="flex flex-wrap gap-2">
              <Button onClick={() => genereaza.mutate()} disabled={genereaza.isPending}>
                {data ? 'Generează altul (cel vechi se oprește)' : 'Generează token'}
              </Button>
              {data && (
                <Button
                  variant="danger"
                  onClick={() => {
                    if (
                      window.confirm(
                        'Revoci tokenul? Programul care îl folosește nu mai poate citi.',
                      )
                    )
                      revoca.mutate(data.id)
                  }}
                  disabled={revoca.isPending}
                >
                  Revocă
                </Button>
              )}
            </div>
          </div>
        </Card>
      )}
    </div>
  )
}
